import { useMemo } from 'react';
import {
  Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text,
} from '@tremor/react';
import { t } from '../lib/i18n';
import type { InventoryStoreRow, Store } from '../api/types';

const fmtMxn = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  minimumFractionDigits: 2,
});

const fmtPct = new Intl.NumberFormat('es-MX', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

interface Props {
  rows: InventoryStoreRow[];
  stores: Store[];
  showCost: boolean;
}

export default function InventoryStoreTable({ rows, stores, showCost }: Props) {
  // No sort UI — store-level table is small (one row per store). Default ordering by sid.
  const sorted = useMemo(() => [...rows].sort((a, b) => a.sid - b.sid), [rows]);
  const nameOf = (sid: number) => stores.find((s) => s.sid === sid)?.name ?? `Tienda ${sid}`;

  if (rows.length === 0) {
    return <Card><Text>{t.inventory.empty}</Text></Card>;
  }

  return (
    <Card className="p-0 overflow-hidden">
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>{t.inventory.cols.sid}</TableHeaderCell>
            {showCost && <TableHeaderCell className="text-right">{t.inventory.cols.invCost}</TableHeaderCell>}
            {showCost && <TableHeaderCell className="text-right">{t.inventory.cols.margin}</TableHeaderCell>}
            {showCost && <TableHeaderCell className="text-right">{t.inventory.cols.gmroi}</TableHeaderCell>}
            <TableHeaderCell className="text-right">{t.inventory.cols.sku}</TableHeaderCell>
            <TableHeaderCell className="text-right">{t.inventory.cols.dead}</TableHeaderCell>
            <TableHeaderCell className="text-right">{t.inventory.cols.low}</TableHeaderCell>
            {showCost && <TableHeaderCell>{t.inventory.cols.abcCounts}</TableHeaderCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((r) => (
            <TableRow key={r.sid}>
              <TableCell>{r.sid} <span className="text-tremor-content text-xs">{nameOf(r.sid)}</span></TableCell>
              {showCost && (
                <TableCell className="text-right">{r.inv_cost_mxn !== undefined ? fmtMxn.format(r.inv_cost_mxn) : '—'}</TableCell>
              )}
              {showCost && (
                <TableCell className="text-right">{r.margin90_mxn !== undefined ? fmtMxn.format(r.margin90_mxn) : '—'}</TableCell>
              )}
              {showCost && (
                <TableCell className="text-right">{r.gmroi == null ? '—' : fmtPct.format(r.gmroi)}</TableCell>
              )}
              <TableCell className="text-right">{r.sku_count}</TableCell>
              <TableCell className="text-right">{r.dead_count > 0 ? <span className="text-red-600 dark:text-red-400">{r.dead_count}</span> : r.dead_count}</TableCell>
              <TableCell className="text-right">{r.low_count > 0 ? <span className="text-amber-600 dark:text-amber-400">{r.low_count}</span> : r.low_count}</TableCell>
              {showCost && (
                <TableCell className="text-xs">
                  {r.abc_a_count ?? 0} / {r.abc_b_count ?? 0} / {r.abc_c_count ?? 0}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
